import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Button, EmptyState, ErrorState, PageHeader, Pill, SelectField, Skeleton, TextField } from "../components/ui.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { get } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { useGuardedNavigate } from "../lib/guard.tsx";
import type { AgendaEventSummary, Group } from "../lib/types.ts";
import { formatDateBR } from "../../shared/dates.ts";
import { COMPLETENESS_LABEL } from "../../shared/agenda.ts";
import { usePageTitle, useUrlState } from "../lib/nav.tsx";
import { FilterPanel } from "../components/nav-controls.tsx";

/** T10: independent agenda. Read-all, edit by editor group; no KR link, weights or review status. */
export function AgendaPage() {
  const plan = usePlan();
  const go = useGuardedNavigate();
  usePageTitle("Agenda", plan.name);
  const [u, setU] = useUrlState({ from: "", to: "", undated: "", committee: "", group_id: "", page: "1", arquivados: "" });
  const { page: pageStr, arquivados, ...f } = u;
  const page = Number(pageStr) || 1;
  const setPage = (fn: (p: number) => number) => setU({ page: String(fn(page)) });
  const showArchived = plan.capabilities.can_manage && arquivados === "1";
  const qs = new URLSearchParams(Object.entries({ ...f, page: String(page), page_size: "50", ...(showArchived ? { include_archived: "true" } : {}) }).filter(([, v]) => v)).toString();
  const q = useQuery({
    queryKey: planKey(plan.id, "agenda", qs),
    queryFn: () => get<{ data: AgendaEventSummary[]; meta: { total: number; page_size: number } }>(`/plans/${plan.id}/agenda/events?${qs}`),
    placeholderData: keepPreviousData,
  });
  const groups = useQuery({ queryKey: planKey(plan.id, "groups"), queryFn: async () => (await get<{ data: Group[] }>(`/plans/${plan.id}/groups`)).data });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setU({ [k]: e.target.value, page: "1" });
  const groupName = (id: string) => (groups.data ?? []).find((g) => g.id === id)?.name ?? "—";
  const chips = [
    ...(f.from || f.to ? [{ label: `Evento: ${f.from ? formatDateBR(f.from) : "…"} a ${f.to ? formatDateBR(f.to) : "…"}`, onRemove: () => setU({ from: "", to: "", page: "1" }) }] : []),
    ...(f.undated ? [{ label: f.undated === "only" ? "Somente sem data" : "Inclui sem data", onRemove: () => setU({ undated: "", page: "1" }) }] : []),
    ...(f.committee ? [{ label: `Comissão: ${f.committee}`, onRemove: () => setU({ committee: "", page: "1" }) }] : []),
    ...(f.group_id ? [{ label: `Grupo: ${groupName(f.group_id)}`, onRemove: () => setU({ group_id: "", page: "1" }) }] : []),
    ...(showArchived ? [{ label: "Mostrando arquivadas", onRemove: () => setU({ arquivados: "" }) }] : []),
  ];
  const canCreate = plan.capabilities.can_manage ? (groups.data ?? []).some((g) => !g.archived_at) : plan.membership.groups.length > 0;
  const dated = Boolean(f.from || f.to);
  const pages = q.data ? Math.max(1, Math.ceil(q.data.meta.total / q.data.meta.page_size)) : 1;

  return (
    <>
      <PageHeader
        title="Agenda da Gestão de Pessoas"
        subtitle="Ações, eventos e suas tarefas com datas. A agenda é independente dos KRs."
        actions={canCreate && <Button onClick={() => go(`/planos/${plan.id}/agenda/nova`)}><Plus className="size-4" aria-hidden />Nova ação da agenda</Button>}
      />
      <FilterPanel chips={chips} onClear={() => setU({ from: "", to: "", undated: "", committee: "", group_id: "", page: "1", arquivados: "" })}>
      <div className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-5">
        <TextField label="Evento a partir de" type="date" value={f.from} onChange={set("from")} />
        <TextField label="Evento até" type="date" value={f.to} onChange={set("to")} />
        <SelectField label="Sem data" value={f.undated} onChange={set("undated")}>
          <option value="">{dated ? "Excluir sem data" : "Incluir sem data"}</option>
          {dated && <option value="include">Incluir sem data</option>}
          <option value="only">Somente sem data</option>
        </SelectField>
        <TextField label="Comissão" value={f.committee} onChange={set("committee")} placeholder="Buscar" />
        <SelectField label="Grupo editor" value={f.group_id} onChange={set("group_id")}>
          <option value="">Todos</option>
          {(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </SelectField>
      </div>
      {plan.capabilities.can_manage && (
        <label className="mb-2 flex min-h-10 items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-primary" checked={showArchived} onChange={(e) => setU({ arquivados: e.target.checked ? "1" : "" })} />
          Mostrar ações arquivadas
        </label>
      )}
      </FilterPanel>
      {q.isPending ? <Skeleton lines={5} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.data.data.length === 0 ? (
        <EmptyState
          title={chips.length ? "Nenhuma ação corresponde aos filtros." : "Ainda não há ações na agenda."}
          action={canCreate && <Button onClick={() => go(`/planos/${plan.id}/agenda/nova`)}>Cadastrar ação da agenda</Button>}
        >
          {!canCreate && (plan.capabilities.can_manage ? "Crie um grupo em Configurações → Grupos para atribuir a edição das ações da agenda." : "Você ainda não faz parte de um grupo editor.")}
        </EmptyState>
      ) : (
        <>
          <ul className="grid gap-3 md:grid-cols-2">
            {q.data.data.map((e) => (
              <li key={e.id}>
                <GuardedLink href={`/planos/${plan.id}/agenda/${e.id}`} className={`lift block h-full rounded-panel border border-line bg-surface p-4 ${e.archived_at ? "opacity-70" : ""}`}>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink">{e.name}</span>
                    {e.archived_at ? <Pill tone="gray">Arquivada</Pill> : e.complete ? <Pill>{COMPLETENESS_LABEL.complete}</Pill> : <Pill tone="gold">{COMPLETENESS_LABEL.incomplete}</Pill>}
                  </span>
                  <span className="mt-1 block text-sm text-ink">{e.event_on ? `Evento em ${formatDateBR(e.event_on)}` : "Data do evento a definir"}</span>
                  <span className="mt-0.5 block text-xs text-muted">
                    {e.committee_name ?? "Comissão a definir"} · {e.target_count ? `${e.target_count} participantes previstos` : "público previsto a definir"} · Grupo {e.editor_group_name}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">
                    {e.task_count} tarefa(s){e.tasks_start && ` · de ${formatDateBR(e.tasks_start)} a ${formatDateBR(e.tasks_end)}`}{!e.can_edit && " · somente consulta"}
                  </span>
                </GuardedLink>
              </li>
            ))}
          </ul>
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span>Página {page} de {pages}</span>
              <Button variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
